import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { RefreshButton } from '../shared/RefreshButton';
import { slack, SlackChannel, SlackMessage, SlackUser, formatSlackTimestamp, parseSlackMessage, parseSlackToMarkdown, isSlackConnected } from '../../services/slack';
import { agentFrontService } from '../../services/agentFront';
import { addWatchedChannel } from '../../hooks/useSlackPolling';
import { Hash, Lock, MessageSquare, Send, AlertCircle, X, Loader2, User as UserIcon, Bot, Bold, Italic, Strikethrough, Link as LinkIcon, List, ListOrdered, Code, Paperclip, File as FileIcon, Image as ImageIcon, ChevronDown, Smile, MoreHorizontal, Search, Bookmark, Check, Sparkles, Info, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
const EMOJIS = ['😀', '😂', '❤️', '👍', '👎', '🎉', '🔥', '💯', '✅', '❌', '🚀', '💡', '🤔', '😎', '👀', '🙏', '💪', '🎯', '📌', '⭐', '🏆', '💬', '📝', '🐛', '🔧', '⚡', '🎨', '📊', '🔒', '🔓', '✨', '🤝', '👋', '🫡', '💀', '😤', '🥳', '🫶', '🤖', '📎', '🔗'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0
  }
};
function formatDateSeparator(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Hoy';
  if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
  return date.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
}
// Regex to extract URLs from text
const URL_REGEX = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
export function SlackPanel() {
  const [isConnected, setIsConnected] = useState(isSlackConnected());
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [activeChannel, setActiveChannel] = useState<SlackChannel | null>(null);
  const [messages, setMessages] = useState<SlackMessage[]>([]);
  const [users, setUsers] = useState<Record<string, SlackUser>>({});
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  // New Features State
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [onlineStatuses, setOnlineStatuses] = useState<Record<string, boolean>>({});
  const [uploadingFile, setUploadingFile] = useState(false);
  // Right Sidebar State
  const [rightTab, setRightTab] = useState<'agent' | 'info'>('agent');
  const [agentMessages, setAgentMessages] = useState<{
    role: 'user' | 'agent';
    content: string;
  }[]>([]);
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [agentInput, setAgentInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const agentMessagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Reset agent messages when channel changes
  useEffect(() => {
    if (activeChannel) {
      setAgentMessages([{
        role: 'agent',
        content: `¡Hola! Soy tu asistente con acceso completo a **Jira**, **Confluence** y **Slack**. Puedo:\n\n• 📊 Consultar épicas, sprints, issues y enviarlos al canal\n• 📝 Buscar documentación en Confluence\n• 💬 Enviar mensajes y resúmenes a #${activeChannel.name}\n• 🔍 Analizar conversaciones del canal\n\n¿En qué te ayudo?`
      }]);
    }
  }, [activeChannel]);
  // Fetch channels on mount
  useEffect(() => {
    if (!isConnected) return;
    let cancelled = false;
    setLoadingChannels(true);
    setError(null);
    slack.getUsers().then(userList => {
      if (cancelled) return;
      const userMap: Record<string, SlackUser> = {};
      const statuses: Record<string, boolean> = {};
      userList.forEach(u => {
        userMap[u.id] = u;
        // Random online status for demo
        statuses[u.id] = Math.random() > 0.3;
      });
      setUsers(userMap);
      setOnlineStatuses(statuses);
    }).catch(console.error);
    slack.getChannels().then(chans => {
      if (cancelled) return;
      const sorted = chans.sort((a, b) => {
        if (a.name === 'general') return -1;
        if (b.name === 'general') return 1;
        return a.name.localeCompare(b.name);
      });
      setChannels(sorted);
      if (sorted.length > 0 && !activeChannel) {
        setActiveChannel(sorted[0]);
      }
      setLoadingChannels(false);
    }).catch(err => {
      if (cancelled) return;
      setError(err.message || 'Error al cargar canales');
      setLoadingChannels(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isConnected]);
  // Fetch messages
  const fetchMessages = async (silent = false) => {
    if (!activeChannel) return;
    if (!silent) setLoadingMessages(true);
    setError(null);
    addWatchedChannel(activeChannel.id);
    try {
      const msgs = await slack.getChannelHistory(activeChannel.id);
      setMessages(msgs.reverse());
      if (!silent) setTimeout(scrollToBottom, 100);
    } catch (err: any) {
      if (!silent) setError(err.message || 'Error al cargar mensajes');
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };
  useEffect(() => {
    fetchMessages();
  }, [activeChannel]);
  // Auto-refresh every 15s
  useEffect(() => {
    if (!activeChannel || !isConnected) return;
    const interval = setInterval(() => {
      fetchMessages(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [activeChannel, isConnected]);
  // Close emoji picker on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollAgentToBottom = () => {
    agentMessagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Textarea auto-resize
  const handleTextareaInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 144)}px`; // max ~6 lines
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };
  const handleAgentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAgentSubmit(agentInput);
    }
  };
  const handleSendMessage = async (e: React.FormEvent | React.KeyboardEvent) => {
    e.preventDefault();
    if (!messageText.trim() || !activeChannel || sendingMessage) return;
    const textToSend = messageText;
    setMessageText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setSendingMessage(true);
    try {
      const newMsg = await slack.sendMessage(activeChannel.id, textToSend);
      setMessages(prev => [...prev, newMsg]);
      setTimeout(scrollToBottom, 100);
      // Fake typing indicator
      setIsTyping(true);
      setTimeout(() => setIsTyping(false), 2000);
    } catch (err: any) {
      setError(err.message || 'Error al enviar mensaje');
      setMessageText(textToSend);
    } finally {
      setSendingMessage(false);
    }
  };
  const handleEmojiSelect = (emoji: string) => {
    setMessageText(prev => prev + emoji);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeChannel) return;
    setUploadingFile(true);
    setError(null);
    try {
      await slack.uploadFile(activeChannel.id, file);
      await fetchMessages(true);
      setTimeout(scrollToBottom, 200);
    } catch (err: any) {
      setError(err.message || 'Error al subir archivo');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const copyToAgent = (text: string, msgId: string) => {
    setRightTab('agent');
    setAgentInput(`Analizá este mensaje:\n"${text}"`);
    setCopiedMessageId(msgId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  };
  const handleAgentSubmit = async (text: string) => {
    if (!text.trim() || isAgentLoading || !activeChannel) return;
    const userMsg = text.trim();
    setAgentInput('');
    setAgentMessages(prev => [...prev, {
      role: 'user',
      content: userMsg
    }]);
    setIsAgentLoading(true);
    setTimeout(scrollAgentToBottom, 100);
    try {
      // Build context from recent Slack messages
      const recentMessages = messages.slice(-20).map(m => {
        const user = m.user ? users[m.user] : null;
        const name = user?.profile?.display_name || user?.name || 'Usuario';
        return `[${formatSlackTimestamp(m.ts).toLocaleTimeString()}] ${name}: ${parseSlackMessage(m.text)}`;
      }).join('\n');
      // Enrich the user message with Slack channel context so the agent knows where to send
      const enrichedMessage = `[CONTEXTO SLACK: Estás en el canal #${activeChannel.name} (ID: ${activeChannel.id}). Últimos mensajes del canal:\n${recentMessages || 'No hay mensajes recientes.'}\n]\n\nPedido del usuario: ${userMsg}`;
      // Build conversation history for the agent
      const history = agentMessages.filter(m => m.role !== 'agent' || !m.content.startsWith('¡Hola!')).map(m => ({
        role: m.role === 'agent' ? 'assistant' : 'user',
        content: m.content
      }));
      // Use the full agentFrontService with all MCP tools (Jira, Confluence, Slack, etc.)
      const result = await agentFrontService.chat(enrichedMessage, history, undefined,
      // no progress callback needed for sidebar
      'agent', undefined);
      setAgentMessages(prev => [...prev, {
        role: 'agent',
        content: result.response
      }]);
      // If the agent sent a message to Slack, refresh the channel messages
      const sentToSlack = result.toolCalls?.some((tc: any) => tc.tool === 'slack_send_message');
      if (sentToSlack) {
        setTimeout(() => fetchMessages(true), 1500);
      }
    } catch (err: any) {
      setAgentMessages(prev => [...prev, {
        role: 'agent',
        content: `⚠️ ${err.message || 'Hubo un error al procesar tu consulta. Intentá de nuevo.'}`
      }]);
    } finally {
      setIsAgentLoading(false);
      setTimeout(scrollAgentToBottom, 100);
    }
  };
  // Filter messages
  const filteredMessages = messages.filter(msg => !searchQuery || parseSlackMessage(msg.text).toLowerCase().includes(searchQuery.toLowerCase()));
  // Group messages by date
  const groupedMessages: {
    dateStr: string;
    messages: SlackMessage[];
  }[] = [];
  let currentDateStr = '';
  let currentGroup: SlackMessage[] = [];
  filteredMessages.forEach(msg => {
    if (msg.subtype === 'channel_join' || msg.subtype === 'channel_leave') return;
    const date = formatSlackTimestamp(msg.ts);
    const dateStr = formatDateSeparator(date);
    if (dateStr !== currentDateStr) {
      if (currentGroup.length > 0) {
        groupedMessages.push({
          dateStr: currentDateStr,
          messages: currentGroup
        });
      }
      currentDateStr = dateStr;
      currentGroup = [msg];
    } else {
      currentGroup.push(msg);
    }
  });
  if (currentGroup.length > 0) {
    groupedMessages.push({
      dateStr: currentDateStr,
      messages: currentGroup
    });
  }
  // Extract files and links for Info tab
  const sharedFiles = messages.flatMap(m => m.files || []).slice(0, 10);
  const sharedLinks = messages.flatMap(m => {
    const text = parseSlackMessage(m.text);
    const matches = text.match(URL_REGEX) || [];
    return matches.map(url => ({
      url,
      ts: m.ts
    }));
  }).slice(0, 10);
  if (!isConnected) {
    return <div className="h-full overflow-y-auto p-4 lg:p-8 bg-[#020617] relative flex items-center justify-center" data-id="element-6288">
        <div className="absolute top-0 left-1/4 w-1/2 h-64 bg-purple-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-6289" />
        <Card className="max-w-md w-full p-8 text-center bg-slate-900/60 backdrop-blur-xl border-slate-800 relative z-10 shadow-2xl" data-id="element-6290">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-purple-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-purple-500/30 shadow-inner" data-id="element-6291">
            <MessageSquare size={36} className="text-purple-400" data-id="element-6292" />
          </div>
          <h2 className="text-2xl font-bold text-slate-100 mb-3" data-id="element-6293">
            Slack Integration
          </h2>
          <p className="text-slate-400 mb-8 text-sm leading-relaxed" data-id="element-6294">
            Configurá tu token de Slack en la sección de Configuración para ver
            canales y enviar mensajes.
          </p>
        </Card>
      </div>;
  }
  return <div className="h-full flex bg-[#020617] relative overflow-hidden font-sans" data-id="element-6295">
      {/* Sidebar: Channels */}
      <div className="w-64 shrink-0 border-r border-slate-800/60 flex flex-col bg-[#0B0F19] z-20" data-id="element-6296">
        {/* Workspace Header */}
        <div className="h-14 px-4 border-b border-slate-800/60 flex items-center justify-between hover:bg-slate-800/30 cursor-pointer transition-colors" data-id="element-6297">
          <h2 className="font-bold text-slate-100 flex items-center gap-2 text-lg" data-id="element-6298">
            VSM Workspace
            <ChevronDown size={14} className="text-slate-400" data-id="element-6299" />
          </h2>
          <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center" data-id="element-6300">
            <RefreshButton onRefresh={() => {
            setLoadingChannels(true);
            slack.getChannels().then(c => {
              setChannels(c);
              setLoadingChannels(false);
            }).catch(() => setLoadingChannels(false));
          }} isRefreshing={loadingChannels} data-id="element-6301" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-3 custom-scrollbar" data-id="element-6302">
          {/* Favoritos Section */}
          <div className="mb-4" data-id="element-6303">
            <button className="w-full px-4 flex items-center gap-1 text-slate-400 hover:text-slate-200 group" data-id="element-6304">
              <ChevronDown size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6305" />

              <span className="text-[11px] font-semibold uppercase tracking-wider" data-id="element-6306">
                Favoritos
              </span>
            </button>
            <div className="px-4 py-1 text-xs text-slate-500 italic" data-id="element-6307">
              No hay favoritos
            </div>
          </div>

          {/* Canales Section */}
          <div className="mb-4" data-id="element-6308">
            <button className="w-full px-4 flex items-center gap-1 text-slate-400 hover:text-slate-200 group mb-1" data-id="element-6309">
              <ChevronDown size={12} data-id="element-6310" />
              <span className="text-[11px] font-semibold uppercase tracking-wider" data-id="element-6311">
                Canales
              </span>
            </button>

            {loadingChannels ? <div className="px-2 space-y-1" data-id="element-6312">
                {Array.from({
              length: 5
            }).map((_, i) => <div key={i} className="h-7 mx-2 bg-slate-800/40 rounded animate-pulse" data-id="element-6313" />)}
              </div> : channels.length === 0 ? <p className="text-xs text-slate-500 px-6 py-1" data-id="element-6314">
                No se encontraron canales.
              </p> : <div className="space-y-0.5 px-2" data-id="element-6315">
                {channels.map(channel => {
              const isActive = activeChannel?.id === channel.id;
              return <button key={channel.id} onClick={() => setActiveChannel(channel)} className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors ${isActive ? 'bg-purple-600 text-white font-medium shadow-sm' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`} data-id="element-6316">
                      {channel.is_private ? <Lock size={14} className={isActive ? 'text-purple-200' : 'text-slate-500'} data-id="element-6317" /> : <Hash size={14} className={isActive ? 'text-purple-200' : 'text-slate-500'} data-id="element-6318" />}
                      <span className="truncate flex-1 text-left" data-id="element-6319">
                        {channel.name}
                      </span>
                      {channel.num_members && channel.num_members > 0 && !isActive && <span className="text-[10px] text-slate-500" data-id="element-6320">
                            {channel.num_members}
                          </span>}
                    </button>;
            })}
              </div>}
          </div>

          {/* Mensajes Directos Section */}
          <div className="mb-4" data-id="element-6321">
            <button className="w-full px-4 flex items-center gap-1 text-slate-400 hover:text-slate-200 group" data-id="element-6322">
              <ChevronDown size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6323" />

              <span className="text-[11px] font-semibold uppercase tracking-wider" data-id="element-6324">
                Mensajes Directos
              </span>
            </button>
            <div className="px-4 py-1 text-xs text-slate-500 italic" data-id="element-6325">
              Próximamente
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-800/60 bg-slate-900/30 flex items-center gap-2" data-id="element-6326">
          <div className="w-6 h-6 rounded bg-purple-900/50 border border-purple-800 flex items-center justify-center text-purple-400 shrink-0" data-id="element-6327">
            <Bot size={14} data-id="element-6328" />
          </div>
          <div className="min-w-0" data-id="element-6329">
            <p className="text-xs font-medium text-slate-300 truncate" data-id="element-6330">
              Conectado como
            </p>
            <p className="text-[10px] text-slate-500 truncate" data-id="element-6331">VSM Bot</p>
          </div>
        </div>
      </div>

      {/* Main Content: Messages */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#0F131F] relative" data-id="element-6332">
        {/* Header */}
        <div className="h-14 shrink-0 border-b border-slate-800/60 flex items-center justify-between px-6 bg-slate-900/50 z-10" data-id="element-6333">
          <div className="flex flex-col justify-center min-w-0 flex-1" data-id="element-6334">
            <div className="flex items-center gap-2" data-id="element-6335">
              {activeChannel?.is_private ? <Lock size={16} className="text-slate-400" data-id="element-6336" /> : <Hash size={16} className="text-slate-400" data-id="element-6337" />}
              <h3 className="font-bold text-slate-100 text-[15px] truncate" data-id="element-6338">
                {activeChannel?.name || 'Selecciona un canal'}
              </h3>
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 ml-2" data-id="element-6339">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-6340" />
                <span className="text-[9px] text-emerald-400 font-medium uppercase tracking-wider" data-id="element-6341">
                  Live
                </span>
              </div>
            </div>
            {activeChannel?.topic?.value && <p className="text-xs text-slate-400 truncate max-w-2xl mt-0.5" data-id="element-6342">
                {activeChannel.topic.value}
              </p>}
          </div>

          <div className="flex items-center gap-3 shrink-0" data-id="element-6343">
            {/* Search Bar */}
            <div className={`flex items-center transition-all duration-300 ${isSearchOpen ? 'w-48 opacity-100' : 'w-0 opacity-0 overflow-hidden'}`} data-id="element-6344">
              <div className="relative w-full" data-id="element-6345">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-6346" />

                <input type="text" placeholder="Buscar..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-md pl-8 pr-3 py-1 text-xs text-slate-200 focus:outline-none focus:border-purple-500" data-id="element-6347" />
              </div>
            </div>

            <button onClick={() => setIsSearchOpen(!isSearchOpen)} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Buscar" data-id="element-6348">
              <Search size={16} data-id="element-6349" />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        <AnimatePresence data-id="element-6350">
          {error && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="absolute top-14 left-0 right-0 mx-6 mt-4 p-3 bg-red-950/40 border border-red-900/50 rounded-xl flex items-center gap-3 text-red-400 z-20 shadow-lg backdrop-blur-md" data-id="element-6351">
              <AlertCircle size={16} className="shrink-0" data-id="element-6352" />
              <p className="text-xs flex-1" data-id="element-6353">{error}</p>
              <button onClick={() => setError(null)} className="p-1 hover:bg-red-900/50 rounded transition-colors" data-id="element-6354">
                <X size={14} data-id="element-6355" />
              </button>
            </motion.div>}
        </AnimatePresence>

        {/* Messages List - FULL WIDTH */}
        <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar w-full" data-id="element-6356">
          {!activeChannel ? <div className="h-full flex flex-col items-center justify-center text-slate-500" data-id="element-6357">
              <MessageSquare size={48} className="mb-4 opacity-20" data-id="element-6358" />
              <p data-id="element-6359">Selecciona un canal para ver los mensajes</p>
            </div> : loadingMessages && messages.length === 0 ? <div className="space-y-6 mt-4 w-full" data-id="element-6360">
              {Array.from({
            length: 5
          }).map((_, i) => <div key={i} className="flex gap-4 w-full" data-id="element-6361">
                  <div className="w-10 h-10 rounded-lg bg-slate-800/50 animate-pulse shrink-0" data-id="element-6362" />
                  <div className="space-y-2 flex-1" data-id="element-6363">
                    <div className="flex gap-2" data-id="element-6364">
                      <div className="h-4 w-24 bg-slate-800/50 rounded animate-pulse" data-id="element-6365" />
                      <div className="h-4 w-16 bg-slate-800/30 rounded animate-pulse" data-id="element-6366" />
                    </div>
                    <div className="h-4 w-3/4 bg-slate-800/40 rounded animate-pulse" data-id="element-6367" />
                    <div className="h-4 w-1/2 bg-slate-800/40 rounded animate-pulse" data-id="element-6368" />
                  </div>
                </div>)}
            </div> : filteredMessages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-slate-500" data-id="element-6369">
              {searchQuery ? <>
                  <Search size={48} className="mb-4 opacity-20" data-id="element-6370" />
                  <p data-id="element-6371">No se encontraron mensajes para "{searchQuery}"</p>
                </> : <p data-id="element-6372">No hay mensajes en este canal.</p>}
            </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-2 pb-4 w-full" data-id="element-6373">
              {groupedMessages.map(group => <div key={group.dateStr} className="w-full" data-id="element-6374">
                  {/* Date Separator */}
                  <div className="relative flex items-center py-6 w-full" data-id="element-6375">
                    <div className="flex-grow border-t border-slate-800" data-id="element-6376"></div>
                    <span className="shrink-0 mx-4 text-[11px] font-bold text-slate-400 bg-slate-900/80 px-4 py-1 rounded-full border border-slate-800/80 shadow-sm" data-id="element-6377">
                      {group.dateStr.charAt(0).toUpperCase() + group.dateStr.slice(1)}
                    </span>
                    <div className="flex-grow border-t border-slate-800" data-id="element-6378"></div>
                  </div>

                  {/* Messages for this date */}
                  <div className="space-y-1 w-full" data-id="element-6379">
                    {group.messages.map(msg => {
                const user = msg.user ? users[msg.user] : null;
                const date = formatSlackTimestamp(msg.ts);
                const isBot = !!msg.bot_id || msg.subtype === 'bot_message';
                const isOnline = user ? onlineStatuses[user.id] : false;
                return <motion.div variants={itemVariants} key={msg.ts} className="flex gap-3 group hover:bg-slate-800/30 p-2 -mx-2 rounded-lg transition-colors relative w-full" data-id="element-6380">
                          {/* Hover Actions Toolbar */}
                          <div className="absolute top-1 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 border border-slate-700 rounded-md shadow-lg flex items-center p-0.5 z-10" data-id="element-6381">
                            <button className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Reaccionar" data-id="element-6382">
                              <Smile size={14} data-id="element-6383" />
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Responder en hilo" data-id="element-6384">
                              <MessageSquare size={14} data-id="element-6385" />
                            </button>
                            <button onClick={() => copyToAgent(parseSlackMessage(msg.text), msg.ts)} className="p-1.5 text-slate-400 hover:text-purple-400 hover:bg-purple-900/30 rounded transition-colors" title="Preguntar al Agente" data-id="element-6386">
                              {copiedMessageId === msg.ts ? <Check size={14} className="text-emerald-400" data-id="element-6387" /> : <Sparkles size={14} data-id="element-6388" />}
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Guardar" data-id="element-6389">
                              <Bookmark size={14} data-id="element-6390" />
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Más opciones" data-id="element-6391">
                              <MoreHorizontal size={14} data-id="element-6392" />
                            </button>
                          </div>

                          {/* Avatar */}
                          <div className="shrink-0 mt-0.5 relative" data-id="element-6393">
                            {user?.profile?.image_48 ? <img src={user.profile.image_48} alt={user.name} className="w-10 h-10 rounded-lg object-cover shadow-sm" data-id="element-6394" /> : isBot ? <div className="w-10 h-10 rounded-lg bg-purple-900/50 border border-purple-800 flex items-center justify-center text-purple-400 shadow-sm" data-id="element-6395">
                                <Bot size={22} data-id="element-6396" />
                              </div> : <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 shadow-sm" data-id="element-6397">
                                <UserIcon size={22} data-id="element-6398" />
                              </div>}
                            {/* Online Status Dot */}
                            {!isBot && <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-[#0F131F] ${isOnline ? 'bg-emerald-500' : 'bg-slate-500'}`} data-id="element-6399" />}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0" data-id="element-6400">
                            <div className="flex items-baseline gap-2 mb-0.5" data-id="element-6401">
                              <span className="font-bold text-slate-200 text-[15px]" data-id="element-6402">
                                {user?.profile?.display_name || user?.real_name || user?.name || (isBot ? 'Bot' : 'Usuario')}
                              </span>
                              {isBot && <span className="text-[9px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" data-id="element-6403">
                                  APP
                                </span>}
                              <span className="text-xs text-slate-500 hover:underline cursor-pointer group/time relative" data-id="element-6404">
                                {date.toLocaleTimeString('es-AR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-slate-200 text-[10px] rounded opacity-0 group-hover/time:opacity-100 pointer-events-none whitespace-nowrap z-20 shadow-lg border border-slate-800" data-id="element-6405">
                                  {date.toLocaleString('es-AR')}
                                </div>
                              </span>
                            </div>

                            <div className="text-[15px] text-slate-300 leading-relaxed break-words prose prose-invert prose-sm max-w-none prose-p:leading-relaxed prose-p:mb-1 prose-pre:bg-slate-900/80 prose-pre:border prose-pre:border-slate-700 prose-code:bg-slate-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-pink-300 prose-code:text-[13px] prose-code:before:content-none prose-code:after:content-none prose-strong:text-slate-100 prose-em:text-slate-200 prose-a:text-purple-400 prose-a:no-underline hover:prose-a:underline prose-ul:my-1 prose-li:my-0" data-id="element-6406">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-6407">
                                {parseSlackToMarkdown(msg.text)}
                              </ReactMarkdown>
                            </div>

                            {/* File Previews */}
                            {msg.files && msg.files.length > 0 && <div className="mt-3 flex flex-wrap gap-3" data-id="element-6408">
                                {msg.files.map((file, idx) => <div key={idx} className="flex items-center gap-3 p-3 rounded-xl border border-slate-800 bg-slate-900/50 max-w-sm hover:bg-slate-800 transition-colors cursor-pointer group/file" data-id="element-6409">
                                    <div className="w-10 h-10 rounded-lg bg-slate-950 flex items-center justify-center shrink-0 border border-slate-800 group-hover/file:border-slate-700 transition-colors" data-id="element-6410">
                                      {file.mimetype?.startsWith('image/') ? <ImageIcon size={20} className="text-purple-400" data-id="element-6411" /> : <FileIcon size={20} className="text-slate-400" data-id="element-6412" />}
                                    </div>
                                    <div className="flex-1 min-w-0" data-id="element-6413">
                                      <p className="text-sm font-medium text-slate-200 truncate group-hover/file:text-purple-400 transition-colors" data-id="element-6414">
                                        {file.name}
                                      </p>
                                      <p className="text-xs text-slate-500 uppercase tracking-wider mt-0.5" data-id="element-6415">
                                        {file.mimetype?.split('/')[1] || 'FILE'}
                                      </p>
                                    </div>
                                  </div>)}
                              </div>}

                            {/* Reactions */}
                            {msg.reactions && msg.reactions.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2.5" data-id="element-6416">
                                {msg.reactions.map(r => <button key={r.name} className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-800/40 hover:bg-slate-800 border border-slate-700/50 hover:border-slate-600 rounded-full text-xs text-slate-300 transition-colors" data-id="element-6417">
                                    <span data-id="element-6418">:{r.name}:</span>
                                    <span className="text-slate-400 font-medium" data-id="element-6419">
                                      {r.count}
                                    </span>
                                  </button>)}
                              </div>}

                            {/* Thread indicator */}
                            {msg.reply_count && msg.reply_count > 0 && <div className="mt-2.5 inline-flex items-center gap-1.5 text-sm text-purple-400 font-medium hover:underline cursor-pointer" data-id="element-6420">
                                <div className="w-6 h-6 rounded bg-purple-500/10 flex items-center justify-center" data-id="element-6421">
                                  <MessageSquare size={12} data-id="element-6422" />
                                </div>
                                {msg.reply_count} respuesta
                                {msg.reply_count !== 1 ? 's' : ''}
                              </div>}
                          </div>
                        </motion.div>;
              })}
                  </div>
                </div>)}

              {/* Typing Indicator */}
              <AnimatePresence data-id="element-6423">
                {isTyping && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="flex items-center gap-2 text-xs text-slate-500 italic mt-4 px-2" data-id="element-6424">
                    <div className="flex gap-1" data-id="element-6425">
                      <span className="w-1.5 h-1.5 bg-slate-500 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-6426" />

                      <span className="w-1.5 h-1.5 bg-slate-500 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-6427" />

                      <span className="w-1.5 h-1.5 bg-slate-500 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-6428" />
                    </div>
                    VSM Bot está escribiendo...
                  </motion.div>}
              </AnimatePresence>

              <div ref={messagesEndRef} data-id="element-6429" />
            </motion.div>}
        </div>

        {/* Message Input Area - FULL WIDTH */}
        <div className="p-4 bg-[#0F131F] shrink-0 w-full relative" data-id="element-6430">
          {/* Emoji Picker Popup */}
          <AnimatePresence data-id="element-6431">
            {showEmojiPicker && <motion.div ref={emojiPickerRef} initial={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} className="absolute bottom-full left-4 mb-2 p-3 bg-slate-900/90 backdrop-blur-xl border border-slate-700 rounded-xl shadow-2xl z-30 w-72" data-id="element-6432">
                <div className="grid grid-cols-8 gap-1" data-id="element-6433">
                  {EMOJIS.map(emoji => <button key={emoji} type="button" onClick={() => handleEmojiSelect(emoji)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-800 rounded text-lg transition-colors" data-id="element-6434">
                      {emoji}
                    </button>)}
                </div>
              </motion.div>}
          </AnimatePresence>

          <form onSubmit={handleSendMessage} className="w-full" data-id="element-6435">
            <div className="bg-slate-900 border border-slate-700 rounded-xl flex flex-col focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500/50 transition-all shadow-sm w-full" data-id="element-6436">
              {/* Formatting Toolbar — functional */}
              <div className="flex items-center gap-1 p-2 border-b border-slate-800/60 bg-slate-900/50 rounded-t-xl overflow-x-auto custom-scrollbar" data-id="element-6437">
                <button type="button" onClick={() => {
                if (!textareaRef.current) return;
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                const sel = messageText.substring(start, end);
                const replacement = sel ? `*${sel}*` : '*texto*';
                setMessageText(messageText.substring(0, start) + replacement + messageText.substring(end));
                setTimeout(() => {
                  ta.focus();
                  ta.setSelectionRange(start + 1, start + 1 + (sel || 'texto').length);
                }, 0);
              }} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Negrita" data-id="element-6438">
                  <Bold size={14} data-id="element-6439" />
                </button>
                <button type="button" onClick={() => {
                if (!textareaRef.current) return;
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                const sel = messageText.substring(start, end);
                const replacement = sel ? `_${sel}_` : '_texto_';
                setMessageText(messageText.substring(0, start) + replacement + messageText.substring(end));
                setTimeout(() => {
                  ta.focus();
                  ta.setSelectionRange(start + 1, start + 1 + (sel || 'texto').length);
                }, 0);
              }} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Cursiva" data-id="element-6440">
                  <Italic size={14} data-id="element-6441" />
                </button>
                <button type="button" onClick={() => {
                if (!textareaRef.current) return;
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                const sel = messageText.substring(start, end);
                const replacement = sel ? `~${sel}~` : '~texto~';
                setMessageText(messageText.substring(0, start) + replacement + messageText.substring(end));
                setTimeout(() => {
                  ta.focus();
                  ta.setSelectionRange(start + 1, start + 1 + (sel || 'texto').length);
                }, 0);
              }} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Tachado" data-id="element-6442">
                  <Strikethrough size={14} data-id="element-6443" />
                </button>
                <div className="w-px h-4 bg-slate-700 mx-1 shrink-0" data-id="element-6444"></div>
                <button type="button" onClick={() => {
                if (!textareaRef.current) return;
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                const sel = messageText.substring(start, end);
                const replacement = sel ? `<${sel}>` : '<https://>';
                setMessageText(messageText.substring(0, start) + replacement + messageText.substring(end));
                setTimeout(() => {
                  ta.focus();
                  const pos = sel ? start + 1 : start + 1;
                  ta.setSelectionRange(start + 1, start + replacement.length - 1);
                }, 0);
              }} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Enlace" data-id="element-6445">
                  <LinkIcon size={14} data-id="element-6446" />
                </button>
                <div className="w-px h-4 bg-slate-700 mx-1 shrink-0" data-id="element-6447"></div>
                <button type="button" onClick={() => {
                if (!textareaRef.current) return;
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const prefix = start > 0 && messageText[start - 1] !== '\n' ? '\n' : '';
                const insert = `${prefix}• `;
                setMessageText(messageText.substring(0, start) + insert + messageText.substring(start));
                setTimeout(() => {
                  ta.focus();
                  const pos = start + insert.length;
                  ta.setSelectionRange(pos, pos);
                }, 0);
              }} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Lista con viñetas" data-id="element-6448">
                  <List size={14} data-id="element-6449" />
                </button>
                <button type="button" onClick={() => {
                if (!textareaRef.current) return;
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const prefix = start > 0 && messageText[start - 1] !== '\n' ? '\n' : '';
                const insert = `${prefix}1. `;
                setMessageText(messageText.substring(0, start) + insert + messageText.substring(start));
                setTimeout(() => {
                  ta.focus();
                  const pos = start + insert.length;
                  ta.setSelectionRange(pos, pos);
                }, 0);
              }} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Lista numerada" data-id="element-6450">
                  <ListOrdered size={14} data-id="element-6451" />
                </button>
                <div className="w-px h-4 bg-slate-700 mx-1 shrink-0" data-id="element-6452"></div>
                <button type="button" onClick={() => {
                if (!textareaRef.current) return;
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                const sel = messageText.substring(start, end);
                const hasNewlines = sel.includes('\n');
                const replacement = hasNewlines ? `\`\`\`\n${sel}\n\`\`\`` : sel ? `\`${sel}\`` : '`código`';
                setMessageText(messageText.substring(0, start) + replacement + messageText.substring(end));
                setTimeout(() => {
                  ta.focus();
                }, 0);
              }} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Bloque de código" data-id="element-6453">
                  <Code size={14} data-id="element-6454" />
                </button>
              </div>

              {/* Textarea */}
              <textarea ref={textareaRef} value={messageText} onChange={e => {
              setMessageText(e.target.value);
              handleTextareaInput();
            }} onKeyDown={handleKeyDown} placeholder={activeChannel ? `Enviar mensaje a #${activeChannel.name}` : 'Selecciona un canal...'} disabled={!activeChannel || sendingMessage} rows={1} className="w-full bg-transparent border-none focus:ring-0 px-4 py-3 text-[15px] text-slate-200 placeholder:text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed resize-none custom-scrollbar" style={{
              minHeight: '48px'
            }} data-id="element-6455" />

              {/* Bottom Actions */}
              <div className="flex items-center justify-between p-2 bg-slate-900/50 rounded-b-xl" data-id="element-6456">
                <div className="flex items-center gap-1" data-id="element-6457">
                  {/* Hidden file input */}
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.json,.md" data-id="element-6458" />

                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={!activeChannel || uploadingFile} className={`p-1.5 rounded transition-colors ${uploadingFile ? 'text-purple-400 animate-pulse' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'} disabled:opacity-50`} title={uploadingFile ? 'Subiendo archivo...' : 'Adjuntar archivo'} data-id="element-6459">
                    {uploadingFile ? <Loader2 size={16} className="animate-spin" data-id="element-6460" /> : <Paperclip size={16} data-id="element-6461" />}
                  </button>
                  <button type="button" onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEmojiPicker(prev => !prev);
                }} className={`p-1.5 rounded transition-colors ${showEmojiPicker ? 'bg-purple-600/30 text-purple-400 ring-1 ring-purple-500/50' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}`} title="Insertar emoji 😀" data-id="element-6462">
                    <Smile size={16} data-id="element-6463" />
                  </button>
                </div>

                <div className="flex items-center gap-2" data-id="element-6464">
                  <button type="button" className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded transition-colors" data-id="element-6465">
                    <MoreHorizontal size={16} data-id="element-6466" />
                  </button>
                  <button type="submit" disabled={!messageText.trim() || !activeChannel || sendingMessage} className="flex items-center justify-center px-3 h-8 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-800 text-white disabled:text-slate-500 rounded transition-colors font-medium text-sm" data-id="element-6467">
                    {sendingMessage ? <Loader2 size={14} className="animate-spin" data-id="element-6468" /> : <div className="flex items-center gap-1.5" data-id="element-6469">
                        <Send size={14} data-id="element-6470" />
                      </div>}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-2 text-right" data-id="element-6471">
              <p className="text-[10px] text-slate-500" data-id="element-6472">
                <strong data-id="element-6473">Enter</strong> para enviar,{' '}
                <strong data-id="element-6474">Shift + Enter</strong> para nueva línea
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* PERMANENT RIGHT SIDEBAR */}
      {activeChannel && <div className="w-[420px] shrink-0 border-l border-slate-800/60 bg-[#0B0F19] flex flex-col z-20" data-id="element-6475">
          {/* Tabs Header */}
          <div className="h-14 px-2 border-b border-slate-800/60 flex items-center shrink-0 bg-slate-900/30" data-id="element-6476">
            <div className="flex p-1 bg-slate-900/80 rounded-lg w-full border border-slate-800" data-id="element-6477">
              <button onClick={() => setRightTab('agent')} className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${rightTab === 'agent' ? 'bg-purple-600/20 text-purple-400 shadow-sm border border-purple-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-id="element-6478">
                <Sparkles size={14} data-id="element-6479" />
                Agente AI
              </button>
              <button onClick={() => setRightTab('info')} className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${rightTab === 'info' ? 'bg-slate-800 text-slate-200 shadow-sm border border-slate-700' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-id="element-6480">
                <Info size={14} data-id="element-6481" />
                Canal
              </button>
            </div>
          </div>

          {/* TAB CONTENT: AGENT */}
          {rightTab === 'agent' && <div className="flex-1 flex flex-col min-h-0 bg-[#0B0F19]" data-id="element-6482">
              {/* Agent Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar" data-id="element-6483">
                {agentMessages.map((msg, idx) => <div key={idx} className="flex gap-3 group" data-id="element-6484">
                    {/* Avatar — same layout as Slack messages */}
                    <div className="shrink-0 mt-0.5 relative" data-id="element-6485">
                      {msg.role === 'agent' ? <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-purple-900/30" data-id="element-6486">
                          <Bot size={20} data-id="element-6487" />
                        </div> : <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-300 border border-slate-700 shadow-sm" data-id="element-6488">
                          <UserIcon size={20} data-id="element-6489" />
                        </div>}
                    </div>

                    {/* Content — same structure as Slack messages */}
                    <div className="flex-1 min-w-0" data-id="element-6490">
                      <div className="flex items-baseline gap-2 mb-0.5" data-id="element-6491">
                        <span className="font-bold text-slate-200 text-[14px]" data-id="element-6492">
                          {msg.role === 'agent' ? 'VSM Agent' : 'Tú'}
                        </span>
                        {msg.role === 'agent' && <span className="text-[9px] bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border border-purple-800/50" data-id="element-6493">
                            AI
                          </span>}
                      </div>
                      <div className={`text-[14px] leading-relaxed whitespace-pre-wrap break-words ${msg.role === 'agent' ? 'text-slate-300' : 'text-slate-200'}`} data-id="element-6494">
                        {msg.role === 'agent' ? <div className="prose prose-invert prose-sm max-w-none prose-p:leading-relaxed prose-p:mb-1.5 prose-pre:bg-slate-900 prose-pre:border prose-pre:border-slate-700 prose-code:bg-slate-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-pink-300 prose-code:text-[13px] prose-code:before:content-none prose-code:after:content-none prose-strong:text-slate-100 prose-em:text-slate-200 prose-a:text-purple-400 prose-ul:my-1 prose-li:my-0" data-id="element-6495">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-6496">
                              {msg.content}
                            </ReactMarkdown>
                          </div> : <p data-id="element-6497">{msg.content}</p>}
                      </div>
                    </div>
                  </div>)}

                {/* Loading state with LED glow effect */}
                {isAgentLoading && <div className="flex gap-3" data-id="element-6498">
                    <div className="shrink-0 mt-0.5 relative" data-id="element-6499">
                      {/* Pulsing LED glow ring */}
                      <div className="absolute -inset-1.5 rounded-xl bg-purple-500/30 animate-pulse blur-md" data-id="element-6500" />
                      <div className="absolute -inset-1 rounded-xl bg-purple-500/20 animate-ping" style={{
                animationDuration: '2s'
              }} data-id="element-6501" />

                      <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-purple-500/50 ring-2 ring-purple-400/60" data-id="element-6502">
                        <Sparkles size={20} className="animate-pulse" data-id="element-6503" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-6504">
                      <div className="flex items-baseline gap-2 mb-0.5" data-id="element-6505">
                        <span className="font-bold text-purple-300 text-[14px]" data-id="element-6506">
                          VSM Agent
                        </span>
                        <span className="text-[9px] bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border border-purple-800/50 animate-pulse" data-id="element-6507">
                          Pensando...
                        </span>
                      </div>
                      {/* Glowing typing indicator */}
                      <div className="relative inline-flex items-center gap-2 mt-1" data-id="element-6508">
                        <div className="absolute -inset-2 rounded-full bg-purple-500/10 blur-lg animate-pulse" data-id="element-6509" />
                        <div className="relative flex gap-1.5 bg-slate-800/60 rounded-full px-4 py-2 border border-purple-500/30 shadow-lg shadow-purple-500/10" data-id="element-6510">
                          <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                    animationDelay: '0ms'
                  }} data-id="element-6511" />

                          <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                    animationDelay: '150ms'
                  }} data-id="element-6512" />

                          <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                    animationDelay: '300ms'
                  }} data-id="element-6513" />
                        </div>
                      </div>
                    </div>
                  </div>}
                <div ref={agentMessagesEndRef} data-id="element-6514" />
              </div>

              {/* Quick Actions — suggestions */}
              {agentMessages.length <= 2 && !isAgentLoading && <div className="px-4 pb-3 flex flex-wrap gap-2" data-id="element-6515">
                  <button onClick={() => handleAgentSubmit('Resumir la conversación reciente')} className="text-[11px] bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 px-3 py-1.5 rounded-full border border-purple-700/40 hover:border-purple-600/60 transition-all shadow-sm hover:shadow-purple-500/10 flex items-center gap-1.5" data-id="element-6516">
                    <Sparkles size={12} data-id="element-6517" />
                    Resumir conversación
                  </button>
                  <button onClick={() => handleAgentSubmit(`Enviá las épicas del proyecto al canal #${activeChannel?.name}`)} className="text-[11px] bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 px-3 py-1.5 rounded-full border border-purple-700/40 hover:border-purple-600/60 transition-all shadow-sm hover:shadow-purple-500/10 flex items-center gap-1.5" data-id="element-6518">
                    <Send size={12} data-id="element-6519" />
                    Enviar épicas al canal
                  </button>
                  <button onClick={() => handleAgentSubmit(`Enviá el estado del sprint actual al canal #${activeChannel?.name}`)} className="text-[11px] bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 px-3 py-1.5 rounded-full border border-purple-700/40 hover:border-purple-600/60 transition-all shadow-sm hover:shadow-purple-500/10 flex items-center gap-1.5" data-id="element-6520">
                    <MessageSquare size={12} data-id="element-6521" />
                    Sprint al canal
                  </button>
                  <button onClick={() => handleAgentSubmit('¿Qué se habló hoy en este canal?')} className="text-[11px] bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 px-3 py-1.5 rounded-full border border-purple-700/40 hover:border-purple-600/60 transition-all shadow-sm hover:shadow-purple-500/10 flex items-center gap-1.5" data-id="element-6522">
                    <Search size={12} data-id="element-6523" />
                    ¿Qué se habló hoy?
                  </button>
                </div>}

              {/* Agent Input — same style as Slack input */}
              <div className="p-4 bg-[#0B0F19] shrink-0 relative" data-id="element-6524">
                <div className="bg-slate-900 border border-slate-700 rounded-xl flex flex-col focus-within:border-purple-500/50 focus-within:ring-1 focus-within:ring-purple-500/50 transition-all shadow-sm w-full" data-id="element-6525">
                  {/* Textarea */}
                  <textarea value={agentInput} onChange={e => setAgentInput(e.target.value)} onKeyDown={handleAgentKeyDown} placeholder={activeChannel ? `Preguntale al agente sobre #${activeChannel.name}` : 'Preguntale al agente...'} disabled={isAgentLoading} rows={1} className="w-full bg-transparent border-none focus:ring-0 px-4 py-3 text-[15px] text-slate-200 placeholder:text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed resize-none custom-scrollbar" style={{
              minHeight: '48px',
              maxHeight: '144px'
            }} data-id="element-6526" />

                  {/* Bottom Actions */}
                  <div className="flex items-center justify-between p-2 bg-slate-900/50 rounded-b-xl" data-id="element-6527">
                    <div className="flex items-center gap-1" data-id="element-6528">
                      <button type="button" className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors" title="Insertar emoji 😀" data-id="element-6529">
                        <Smile size={16} data-id="element-6530" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2" data-id="element-6531">
                      <button type="button" className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded transition-colors" data-id="element-6532">
                        <MoreHorizontal size={16} data-id="element-6533" />
                      </button>
                      <button onClick={() => handleAgentSubmit(agentInput)} disabled={!agentInput.trim() || isAgentLoading} className="flex items-center justify-center px-3 h-8 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-800 text-white disabled:text-slate-500 rounded transition-colors font-medium text-sm" data-id="element-6534">
                        {isAgentLoading ? <Loader2 size={14} className="animate-spin" data-id="element-6535" /> : <Send size={14} data-id="element-6536" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-right" data-id="element-6537">
                  <p className="text-[10px] text-slate-500" data-id="element-6538">
                    <strong data-id="element-6539">Enter</strong> para enviar,{' '}
                    <strong data-id="element-6540">Shift + Enter</strong> para nueva línea
                  </p>
                </div>
              </div>
            </div>}

          {/* TAB CONTENT: INFO */}
          {rightTab === 'info' && <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-6" data-id="element-6541">
              {/* About */}
              <div data-id="element-6542">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-6543">
                  Acerca de
                </h4>
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 space-y-3" data-id="element-6544">
                  <div data-id="element-6545">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-6546">Nombre</p>
                    <p className="text-sm text-slate-200 flex items-center gap-1.5" data-id="element-6547">
                      {activeChannel.is_private ? <Lock size={14} data-id="element-6548" /> : <Hash size={14} data-id="element-6549" />}
                      {activeChannel.name}
                    </p>
                  </div>
                  {activeChannel.topic?.value && <div data-id="element-6550">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-6551">Tema</p>
                      <p className="text-sm text-slate-300" data-id="element-6552">
                        {activeChannel.topic.value}
                      </p>
                    </div>}
                  {activeChannel.purpose?.value && <div data-id="element-6553">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-6554">Propósito</p>
                      <p className="text-sm text-slate-300" data-id="element-6555">
                        {activeChannel.purpose.value}
                      </p>
                    </div>}
                </div>
              </div>

              {/* Members */}
              <div data-id="element-6556">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center justify-between" data-id="element-6557">
                  Miembros
                  <span className="bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded text-[10px]" data-id="element-6558">
                    {activeChannel.num_members || 0}
                  </span>
                </h4>
                <div className="space-y-2" data-id="element-6559">
                  {Object.values(users).slice(0, 15).map(user => <div key={user.id} className="flex items-center gap-2.5 p-1.5 hover:bg-slate-800/50 rounded-lg cursor-pointer transition-colors" data-id="element-6560">
                        <div className="relative" data-id="element-6561">
                          {user.profile?.image_48 ? <img src={user.profile.image_48} alt={user.name} className="w-7 h-7 rounded object-cover" data-id="element-6562" /> : <div className="w-7 h-7 rounded bg-slate-800 flex items-center justify-center text-slate-400" data-id="element-6563">
                              <UserIcon size={14} data-id="element-6564" />
                            </div>}
                          {!user.is_bot && <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0B0F19] ${onlineStatuses[user.id] ? 'bg-emerald-500' : 'bg-slate-500'}`} data-id="element-6565" />}
                        </div>
                        <div className="min-w-0 flex-1" data-id="element-6566">
                          <p className="text-sm text-slate-200 truncate font-medium" data-id="element-6567">
                            {user.profile?.display_name || user.real_name || user.name}
                          </p>
                          {user.profile?.status_text && <p className="text-[10px] text-slate-500 truncate" data-id="element-6568">
                              {user.profile.status_emoji}{' '}
                              {user.profile.status_text}
                            </p>}
                        </div>
                      </div>)}
                </div>
              </div>

              {/* Shared Files */}
              {sharedFiles.length > 0 && <div data-id="element-6569">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-6570">
                    Archivos Recientes
                  </h4>
                  <div className="space-y-2" data-id="element-6571">
                    {sharedFiles.map((file, idx) => <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors cursor-pointer group" data-id="element-6572">
                        <div className="w-8 h-8 rounded bg-slate-900 flex items-center justify-center shrink-0 border border-slate-800 group-hover:border-slate-700" data-id="element-6573">
                          {file.mimetype?.startsWith('image/') ? <ImageIcon size={14} className="text-purple-400" data-id="element-6574" /> : <FileIcon size={14} className="text-slate-400" data-id="element-6575" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-6576">
                          <p className="text-xs font-medium text-slate-200 truncate group-hover:text-purple-400 transition-colors" data-id="element-6577">
                            {file.name}
                          </p>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5" data-id="element-6578">
                            {file.mimetype?.split('/')[1] || 'FILE'}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>}

              {/* Shared Links */}
              {sharedLinks.length > 0 && <div data-id="element-6579">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-6580">
                    Links Recientes
                  </h4>
                  <div className="space-y-2" data-id="element-6581">
                    {sharedLinks.map((link, idx) => {
              try {
                const urlObj = new URL(link.url);
                return <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors group" data-id="element-6582">
                            <div className="w-8 h-8 rounded bg-slate-900 flex items-center justify-center shrink-0 border border-slate-800 group-hover:border-slate-700" data-id="element-6583">
                              <LinkIcon size={14} className="text-blue-400" data-id="element-6584" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-6585">
                              <p className="text-xs font-medium text-slate-200 truncate group-hover:text-blue-400 transition-colors" data-id="element-6586">
                                {urlObj.hostname}
                              </p>
                              <p className="text-[10px] text-slate-500 truncate mt-0.5" data-id="element-6587">
                                {urlObj.pathname}
                              </p>
                            </div>
                            <ExternalLink size={12} className="text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6588" />
                          </a>;
              } catch {
                return null;
              }
            })}
                  </div>
                </div>}
            </div>}
        </div>}
    </div>;
}